import React, { useEffect, useState } from 'react';
import { X, Loader2, FileText } from 'lucide-react';

import SafeHtml from './SafeHtml';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LegalDocModal — shared overlay for displaying any legal document
 * (terms, privacy, license, refund) configured in Admin → Settings →
 * Legal documents. Lazily fetches the HTML on open, keeps the markup
 * scrollable, and dismisses on backdrop click / Esc.
 *
 * Props:
 *   slug    — one of "terms" | "privacy" | "license" | "refund"
 *   open    — boolean
 *   onClose — () => void
 */
export default function LegalDocModal({ slug, open, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !slug) return;
    let stop = false;
    (async () => {
      setLoading(true); setErr(null); setDoc(null);
      try {
        const r = await fetch(`${API_URL}/api/config/legal/${slug}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!stop) setDoc(j);
      } catch (e) {
        if (!stop) setErr(e.message);
      } finally {
        if (!stop) setLoading(false);
      }
    })();
    return () => { stop = true; };
  }, [open, slug]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="legal-doc-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-5 h-5 text-cyan-600 flex-shrink-0" />
            <h3 className="font-bold text-slate-800 truncate" data-testid="legal-doc-title">
              {doc?.title || (loading ? 'Loading…' : 'Legal document')}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="legal-doc-close"
            className="text-slate-400 hover:text-slate-700 p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-5 sm:py-6">
          {loading && (
            <div className="text-center text-slate-500 py-12">
              <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
            </div>
          )}
          {err && !loading && (
            <div className="text-center text-rose-600 py-12 text-sm" data-testid="legal-doc-error">
              Failed to load: {err}
            </div>
          )}
          {doc && !loading && !err && (
            <SafeHtml as="div" className="prose prose-slate max-w-none text-sm leading-relaxed
                         prose-headings:font-bold prose-headings:text-slate-800
                         prose-h2:text-xl prose-h3:text-base
                         prose-a:text-cyan-600 prose-a:underline
                         prose-strong:text-slate-800 prose-li:my-0.5" 
              data-testid="legal-doc-body" html={doc.html || ''} />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 sm:px-6 py-3 flex justify-end">
          <button
            onClick={onClose}
            data-testid="legal-doc-footer-close"
            className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-bold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
